import React, { useState, useEffect } from 'react';
import './App.css';
import Header from './components/Header';
import DrugMarket from './components/DrugMarket';
import TravelSection from './components/TravelSection';
import ActionsSection from './components/ActionsSection';
import AssetsMarket from './components/AssetsMarket';
import GameOverDialog from './components/GameOverDialog';
import LeaderboardDialog from './components/LeaderboardDialog';
import HowToPlayDialog from './components/HowToPlayDialog';
import StudioFooter from './components/StudioFooter';
import { initialPlayerState, availableAssets, CONFIG, generateMarketPrices, isBankrupt } from './gameData';

// Swap to 'background2.png' to try the alternate alley art.
const BACKGROUND_IMAGE = 'background.png';

function App() {
  const [player, setPlayer] = useState(initialPlayerState);
  const [prices, setPrices] = useState({});
  const [events, setEvents] = useState([]);
  const [gameEnded, setGameEnded] = useState(false);
  const [endReason, setEndReason] = useState('time'); // 'time' | 'bankrupt'
  const [showDialog, setShowDialog] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showHowTo, setShowHowTo] = useState(false);

  // Regenerate the market for a given location and surface any event messages.
  const refreshMarket = (location) => {
    const { prices: newPrices, events: newEvents } = generateMarketPrices(location);
    setPrices(newPrices);
    setEvents(newEvents);
  };

  useEffect(() => {
    refreshMarket(initialPlayerState.location);
  }, []);

  // Set the alley background at runtime (it's a public/ asset, not bundled).
  useEffect(() => {
    document.body.style.backgroundImage = `url('${process.env.PUBLIC_URL}/${BACKGROUND_IMAGE}')`;
  }, []);

  // End the game once the player runs past the final day. Driven off player
  // state via an effect rather than as a side effect inside a state updater.
  useEffect(() => {
    if (player.day > player.maxDays && !gameEnded) {
      setEndReason('time');
      setGameEnded(true);
      setShowDialog(true);
    }
  }, [player.day, player.maxDays, gameEnded]);

  // End the game early if the player hits a dead end: broke, nothing to sell,
  // and maxed out on borrowing. The loan sharks collect.
  useEffect(() => {
    if (!gameEnded && isBankrupt(player)) {
      setEndReason('bankrupt');
      setGameEnded(true);
      setShowDialog(true);
    }
  }, [player, gameEnded]);

  const resetGame = () => {
    setPlayer(initialPlayerState);
    setGameEnded(false);
    setEndReason('time');
    setShowDialog(false);
    refreshMarket(initialPlayerState.location);
  };

  // Advance one day. Pass a destination to also travel there (prices reflect
  // wherever you end up); omit it to stay put (the End Day action).
  const advanceDay = (nextLocation) => {
    setPlayer(prev => {
      // Don't advance past the end; the effect above handles ending the game.
      if (prev.day > prev.maxDays) return prev;

      const newDebt = prev.debt > 0 ? Math.ceil(prev.debt * (1 + CONFIG.dailyInterest)) : 0;
      return {
        ...prev,
        day: prev.day + 1,
        debt: newDebt,
        location: nextLocation || prev.location,
      };
    });
    refreshMarket(nextLocation || player.location);
  };

  return (
    <div className="App">
      <Header
        player={player}
        onShowLeaderboard={() => setShowLeaderboard(true)}
        onShowHowTo={() => setShowHowTo(true)}
      />
      
      <div id="game-container">
        {events.length > 0 && (
          <div className="market-events">
            {events.map((msg, i) => (
              <p key={i} className="market-event">{msg}</p>
            ))}
          </div>
        )}

        <DrugMarket
          player={player}
          setPlayer={setPlayer}
          prices={prices}
          gameEnded={gameEnded}
        />

        <TravelSection 
          player={player}
          setPlayer={setPlayer}
          advanceDay={advanceDay}
          gameEnded={gameEnded}
        />

        <ActionsSection 
          player={player}
          setPlayer={setPlayer}
          advanceDay={advanceDay}
          gameEnded={gameEnded}
        />

        <AssetsMarket 
          player={player}
          setPlayer={setPlayer}
          availableAssets={availableAssets}
          gameEnded={gameEnded}
        />
      </div>

      <GameOverDialog
        show={showDialog}
        onClose={() => setShowDialog(false)}
        onPlayAgain={resetGame}
        player={player}
        reason={endReason}
      />

      <LeaderboardDialog
        show={showLeaderboard}
        onClose={() => setShowLeaderboard(false)}
      />

      <HowToPlayDialog
        show={showHowTo}
        onClose={() => setShowHowTo(false)}
      />

      <StudioFooter />
    </div>
  );
}

export default App;
